import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router";
import { NodeRefFragment } from "@graphql";
import {
  CONSULTANT_SLIDER,
  ConsultantContractEditorLocation,
  ConsultantEditorLocation,
} from "@navigation";
import { erpPrefillFromSearch } from "./ConsultantContract/erpPrefill";
import { store } from "@store";
import { checkIfDirectRoute, pushToNodeItem } from "@utils/navigationUtils";
import AvailableConsultantSearch from "./AvailableConsultantSearch/AvailableConsultantSearch";
import { parse } from "qs";
import { ConsultantProfileLocation } from "@navigation/locations/consultant-profile.location";
import { Box, ThemeProvider } from "@material-ui/core";
import { bonlivaPipelineTheme } from "@bonliva-crm/ui";
import { ConsultantSearchFiltersProvider } from "./consultants-search-new/hooks/use-consultant-search-filters";
import { ConsultantSearchNew } from "./consultants-search-new/consultant-search-new";

type Props = RouteComponentProps<any>;

export function ConsultantSearchPage(props: Props) {
  const {
    match,
    location: { search }
  } = props;
  const { url } = match;
  const { navigation } = store();

  const { searchProfileId } = parse(search, { ignoreQueryPrefix: true });

  const searchProps = useMemo(() => {
    return {
      onItemOpen: (id: string, search: string) => {
        return navigation.open(CONSULTANT_SLIDER, new ConsultantProfileLocation(id));
      },
      onNewItem: () => {
        navigation.open(CONSULTANT_SLIDER, new ConsultantEditorLocation(null));
      },
      onNodeClick: (nodeRef: NodeRefFragment) => {
        console.log("nodeRef", nodeRef);
        pushToNodeItem(nodeRef);
      },
      searchProfileId: searchProfileId as string
    };
  }, [navigation, searchProfileId]);

  return (
    <>
      {catchDirectUrl(url)}
      {url.includes("available") ? (
        <AvailableConsultantSearch {...searchProps} />
      ) : (
        <ThemeProvider theme={bonlivaPipelineTheme}>
          <ConsultantSearchFiltersProvider>
            <Box
              width="100%"
              height="100%"
              fontFamily={bonlivaPipelineTheme.typography.fontFamily}
            >
              <ConsultantSearchNew />
            </Box>
          </ConsultantSearchFiltersProvider>
        </ThemeProvider>
      )}
    </>
  );
}

function catchDirectUrl(url) {
  return checkIfDirectRoute(
    [
      // ERP-741 — `/consultants/:id/contract/new?…` is how the ERP links here
      // when it is not embedded in this app. Listed first: the generic pattern
      // below would otherwise swallow it with action1 = "contract".
      `${url}/:consultantId/contract/:contractId?`,
      `${url}/:consultantId/:action1?`
    ],
    CONSULTANT_SLIDER,
    ({ action1, consultantId, contractId }) => {
      if (contractId !== undefined) {
        return new ConsultantContractEditorLocation(
          consultantId,
          contractId === "new" ? "" : contractId,
          contractId === "new" ? "add" : "edit",
          // The prefill rides in the query string on this path — the direct
          // route only ever carries path params.
          erpPrefillFromSearch(window.location.search)
        );
      }

      const action = action1 || "view";
      const realId = consultantId !== "new" ? consultantId : null;
      if (consultantId === "new" || (consultantId && action === "edit")) {
        return new ConsultantEditorLocation(realId);
      }

      if (consultantId && action === "view") {
        return new ConsultantProfileLocation(realId);
      }
    }
  );
}
